import { useState, useEffect } from 'react';
import { supabase } from '../../infrastructure/supabase/supabaseClient';

const PENDING_INVITE_KEY = 'aura_pending_invite_code';

export const OnboardingStorage = {
  savePendingInvite: async (code: string) => {
    try {
      if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
        (globalThis as any).localStorage.setItem(PENDING_INVITE_KEY, code);
      }
    } catch (e) { console.error('LocalStorage error', e); }
  },
  getPendingInvite: async () => {
    try {
      if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
        return (globalThis as any).localStorage.getItem(PENDING_INVITE_KEY);
      }
      return null;
    } catch (e) { 
      console.warn('Error reading pending invite', e);
      return null; 
    }
  },
  clearPendingInvite: async () => {
    try {
      if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
        (globalThis as any).localStorage.removeItem(PENDING_INVITE_KEY);
      }
    } catch (e) { 
      console.warn('Error clearing pending invite', e);
    }
  }
};

export interface OnboardingPayload {
  rol_solicitado: string; // 'VETERINARIO', 'MEDICO', etc.
  titulo_principal: string;
  resumen_profesional: string;
  atiende_virtual: boolean;
  atiende_presencial: boolean;
  atiende_domicilio: boolean;
  especialidades_ids: number[];
  credencial_mpps?: string;
  credencial_cmv?: string;
  credencial_fmv?: string;
  credencial_fpv?: string;
  credencial_runsai?: string;
}

export function useClinicalOnboarding() {
  // Estado para la Validación Perezosa (Lazy Validation)
  // Este código se guarda en la memoria RAM del teléfono y no se envía hasta el final.
  const [magicCodeInMemory, setMagicCodeInMemory] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Si estamos en entorno Web, extraemos directamente el parámetro 'code'
    if ((globalThis as any).window !== undefined) {
      const urlParams = new URLSearchParams((globalThis as any).globalThis.location.search);
      const codeFromUrl = urlParams.get('code');
      if (codeFromUrl?.length === 8) {
        console.log('[Lazy Validation] Código interceptado desde URL:', codeFromUrl);
        setMagicCodeInMemory(codeFromUrl.toUpperCase());
        return; // Prioridad al de la URL
      }
    }

    // Si no está en la URL, revisar el OnboardingStorage (Memoria Persistente)
    OnboardingStorage.getPendingInvite().then(storedCode => {
      if (storedCode?.length === 8) {
        console.log('[Lazy Validation] Código rescatado de Storage persistente:', storedCode);
        setMagicCodeInMemory(storedCode.toUpperCase());
      }
    });
  }, []);

  /**
   * Método público para inyectar el código en la memoria RAM.
   * Útil cuando se captura el link desde WhatsApp.
   */
  const storeMagicCode = (code: string) => {
    console.log('[Lazy Validation] Código capturado en memoria RAM:', code);
    setMagicCodeInMemory(code);
  };

  /**
   * Dispara el payload atómico al backend. Si falla la red, el código sigue en RAM.
   */
  const submitAtomicOnboarding = async (payload: OnboardingPayload) => {
    if (!magicCodeInMemory) {
      setError('No tienes un código de invitación válido en memoria.');
      return false;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      console.log('[Atomic Transaction] Disparando RPC maestro...');
      
      const { error: rpcError } = await supabase.rpc('fn_onboarding_clinico_completo', {
        p_codigo_invitacion: magicCodeInMemory,
        p_rol_solicitado: payload.rol_solicitado,
        p_titulo_principal: payload.titulo_principal,
        p_resumen_profesional: payload.resumen_profesional,
        p_atiende_virtual: payload.atiende_virtual,
        p_atiende_presencial: payload.atiende_presencial,
        p_atiende_domicilio: payload.atiende_domicilio,
        p_especialidades_ids: payload.especialidades_ids,
        p_credencial_mpps: payload.credencial_mpps || null,
        p_credencial_cmv: payload.credencial_cmv || null,
        p_credencial_fmv: payload.credencial_fmv || null,
        p_credencial_fpv: payload.credencial_fpv || null,
        p_credencial_runsai: payload.credencial_runsai || null
      });

      if (rpcError) throw rpcError;

      console.log('[Atomic Transaction] ÉXITO: Perfil clínico creado sin fricción.');
      // Opcional: Limpiar la memoria solo en caso de éxito total
      setMagicCodeInMemory(null);
      return true;

    } catch (err: any) {
      console.error('[Atomic Transaction] Falla. Ejecutando ROLLBACK. Código sigue intacto.', err.message);
      setError(err.message || 'Error de conexión. Tu código de invitación sigue válido, intenta de nuevo.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyPendingInvite = async (code: string) => {
    try {
      const { data, error } = await supabase.rpc('fn_verificar_codigo_invitacion', {
        p_codigo_invitacion: code
      });
      
      if (error) throw error;
      return data;
    } catch (err: any) {
      console.error('[Early Validation] Error verificando código:', err.message);
      return { es_valido: false, bloqueado: false, intentos_restantes: 0 };
    }
  };

  return {
    magicCodeInMemory,
    storeMagicCode,
    submitAtomicOnboarding,
    verifyPendingInvite,
    isSubmitting,
    error
  };
}
